import { enqueueSnackbar } from 'notistack';
import { useMemo, useState, useEffect } from 'react';

import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Stack from '@mui/material/Stack';
import Switch from '@mui/material/Switch';
import TextField from '@mui/material/TextField';
import CardHeader from '@mui/material/CardHeader';
import Typography from '@mui/material/Typography';
import LoadingButton from '@mui/lab/LoadingButton';
import FormControlLabel from '@mui/material/FormControlLabel';

import { useTranslate } from 'src/locales';
import { PackSettings } from 'src/services/presentations/presentations.types';
import { useGetPresentationSettingsQuery, useUpdatePresentationSettingsMutation } from 'src/services/presentations/presentations.service';

import ComparisonRowsPicker, { rowsOn, rowsLayout, ComparisonRowsValue } from './comparison-rows-picker';

/** The most a heading and the note can hold (backend MAX_PACK_HEADING and MAX_PACK_INTRO). */
const MAX_HEADING = 120;
const MAX_INTRO = 2000;

type Draft = Omit<PackSettings, 'comparison_rows'> & { rows: ComparisonRowsValue };

const draftFrom = (packs?: PackSettings): Draft => ({
  shortlist_heading: packs?.shortlist_heading ?? '',
  comparison_heading: packs?.comparison_heading ?? '',
  intro: packs?.intro ?? '',
  show_candidates_table: packs?.show_candidates_table ?? true,
  rows: rowsLayout(packs?.comparison_rows),
});

const packsFrom = ({ rows, ...draft }: Draft): PackSettings => ({ ...draft, comparison_rows: rowsOn(rows) });

/**
 * How the agency's shortlist packs and comparison reports open (JT1-1887, staging feedback), on
 * Settings → Brand for admins: the cover headings, a note under them, whether a shortlist pack lists
 * its candidates in a table, and the rows a new comparison starts with. A pack's design comes from
 * the agency's default template.
 */
export default function PackSettingsCard() {
  const { t } = useTranslate();
  const { currentData: settings } = useGetPresentationSettingsQuery();
  const [updateSettings, { isLoading }] = useUpdatePresentationSettingsMutation();
  const [draft, setDraft] = useState<Draft | null>(null);

  const saved = useMemo(() => (settings ? draftFrom(settings.packs) : null), [settings]);

  useEffect(() => {
    if (saved && !draft) setDraft(saved);
  }, [saved, draft]);

  if (!draft) return null;

  const changed = !!saved && JSON.stringify(packsFrom(draft)) !== JSON.stringify(packsFrom(saved));
  const noRows = !rowsOn(draft.rows).length;
  const set = (next: Partial<Draft>) => setDraft({ ...draft, ...next });

  const handleSave = async () => {
    try {
      const result = await updateSettings({ packs: packsFrom(draft) }).unwrap();
      // Kept as the server tidied it, so an emptied heading shows the default again.
      setDraft(draftFrom(result.packs));
      enqueueSnackbar(t('presentations.pack_settings.saved'), { variant: 'success' });
    } catch {
      enqueueSnackbar(t('presentations.templates.failed'), { variant: 'error' });
    }
  };

  return (
    <Card sx={{ mt: 3 }}>
      <CardHeader title={t('presentations.pack_settings.title')} subheader={t('presentations.pack_settings.subheader')} />
      <Stack spacing={2.5} sx={{ p: 3 }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
          <TextField
            fullWidth
            label={t('presentations.pack_settings.shortlist_heading')}
            placeholder={t('presentations.pack_settings.shortlist_heading_default')}
            value={draft.shortlist_heading}
            onChange={(event) => set({ shortlist_heading: event.target.value })}
            inputProps={{ maxLength: MAX_HEADING }}
          />
          <TextField
            fullWidth
            label={t('presentations.pack_settings.comparison_heading')}
            placeholder={t('presentations.pack_settings.comparison_heading_default')}
            value={draft.comparison_heading}
            onChange={(event) => set({ comparison_heading: event.target.value })}
            inputProps={{ maxLength: MAX_HEADING }}
          />
        </Stack>

        <TextField
          fullWidth
          multiline
          minRows={3}
          label={t('presentations.pack_settings.intro')}
          helperText={t('presentations.pack_settings.intro_helper')}
          value={draft.intro}
          onChange={(event) => set({ intro: event.target.value })}
          inputProps={{ maxLength: MAX_INTRO }}
        />

        <FormControlLabel
          control={<Switch checked={draft.show_candidates_table} onChange={(event) => set({ show_candidates_table: event.target.checked })} />}
          label={(
            <Box>
              <Typography variant="subtitle2">{t('presentations.pack_settings.candidates_table')}</Typography>
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>{t('presentations.pack_settings.candidates_table_helper')}</Typography>
            </Box>
          )}
        />

        <Box>
          <Typography variant="subtitle2">{t('presentations.pack_settings.rows')}</Typography>
          <Typography variant="caption" component="p" sx={{ color: noRows ? 'error.main' : 'text.secondary', mb: 1 }}>
            {t(noRows ? 'presentations.pack_settings.no_rows' : 'presentations.pack_settings.rows_helper')}
          </Typography>
          <ComparisonRowsPicker value={draft.rows} onChange={(rows) => set({ rows })} />
        </Box>

        <Stack direction="row" justifyContent="flex-end">
          <LoadingButton variant="contained" loading={isLoading} disabled={!changed || noRows} onClick={handleSave}>
            {t('common.save')}
          </LoadingButton>
        </Stack>
      </Stack>
    </Card>
  );
}
